import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getEmployeeDashboard,
  getEmployeeTimesheets,
  createEmployeeAccount,
  removeEmployeeAccount,
  resetEmployeePassword,
  updateEmployeeAccountStatus,
  updateEmployeeAccountRole,
  updateEmployeeStatus,
  restoreEmployee,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  ArrowLeft,
  User,
  ChartLine,
  Users,
  Clock,
  Briefcase,
  CurrencyDollar,
  Gear,
  Folder,
  ListChecks,
  TrendUp,
  TrendDown,
  Warning,
  CheckCircle,
  Eye,
  Envelope,
  Phone,
  MapPin,
  IdentificationCard,
  Key,
  UserCirclePlus,
  UserCircleMinus,
  Shield,
  LockKey,
  Power,
  Archive,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// Stat Card Component
function StatCard({ icon: Icon, label, value, subValue, color = 'cyan', trend }) {
  const colorClasses = {
    cyan: 'from-[#3AB8D6]/20 to-[#3AB8D6]/5 border-[#3AB8D6]/30',
    gold: 'from-[#E2B05E]/20 to-[#E2B05E]/5 border-[#E2B05E]/30',
    green: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30',
    red: 'from-red-500/20 to-red-500/5 border-red-500/30',
    purple: 'from-purple-500/20 to-purple-500/5 border-purple-500/30',
  };
  
  return (
    <div className={`valhalla-card p-4 bg-gradient-to-br ${colorClasses[color]} border`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 text-[#9CA3AF]" />
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend >= 0 ? <TrendUp className="w-3 h-3" /> : <TrendDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-[#F3F4F6]">{value}</p>
      <p className="text-xs text-[#9CA3AF] mt-1">{label}</p>
      {subValue && <p className="text-xs text-[#6B7280] mt-0.5">{subValue}</p>}
    </div>
  );
}

// Overview Tab Component
function OverviewTab({ dashboard, loading, onRefresh }) {
  const { t, isSpanish } = useLanguage();
  
  if (loading || !dashboard) {
    return <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>;
  }
  
  const { employee, user_account, projects, hours_this_month, tasks, billing } = dashboard;
  
  const getStatusColor = (status) => {
    const colors = {
      activo: 'bg-emerald-500/20 text-emerald-400',
      inactivo: 'bg-gray-500/20 text-gray-400',
      suspendido: 'bg-amber-500/20 text-amber-400',
      en_licencia: 'bg-blue-500/20 text-blue-400',
      terminado: 'bg-red-500/20 text-red-400',
      archivado: 'bg-gray-500/20 text-gray-400',
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };
  
  return (
    <div className="space-y-6">
      {/* Employee Info Header */}
      <div className="valhalla-card p-6">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[#3AB8D6] to-[#2A8FA8] flex items-center justify-center text-white font-bold text-3xl">
            {employee.name?.charAt(0)?.toUpperCase()}{employee.last_name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-[#F3F4F6]">{employee.name} {employee.last_name}</h2>
              <Badge className={getStatusColor(employee.status)}>{employee.status}</Badge>
              {user_account && (
                <Badge className="bg-purple-500/20 text-purple-400">
                  <Key className="w-3 h-3 mr-1" />
                  {user_account.role}
                </Badge>
              )}
            </div>
            <p className="text-[#E2B05E] font-medium">{employee.position}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm text-[#9CA3AF]">
              <div className="flex items-center gap-2">
                <Envelope className="w-4 h-4" />
                <span>{employee.email}</span>
              </div>
              {employee.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>{employee.phone}</span>
                </div>
              )}
              {employee.department && (
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  <span>{employee.department}</span>
                </div>
              )}
              {employee.document_id && (
                <div className="flex items-center gap-2">
                  <IdentificationCard className="w-4 h-4" />
                  <span>{employee.document_id}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-3">
              <Badge variant="outline" className="border-[#242E42]">{employee.level}</Badge>
              <Badge variant="outline" className="border-[#242E42]">{employee.modality}</Badge>
              <Badge variant="outline" className="border-[#242E42]">{employee.payment_type}</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          icon={Folder}
          label={isSpanish ? "Proyectos Activos" : "Active Projects"}
          value={projects.active}
          subValue={`${projects.total} ${isSpanish ? 'total' : 'total'}`}
          color="cyan"
        />
        <StatCard
          icon={Clock}
          label={isSpanish ? "Horas Este Mes" : "Hours This Month"}
          value={hours_this_month.total?.toFixed(1) || 0}
          subValue={`${hours_this_month.billable?.toFixed(1) || 0} ${isSpanish ? 'facturables' : 'billable'}`}
          color="gold"
        />
        <StatCard
          icon={ChartLine}
          label={t('utilization')}
          value={`${hours_this_month.utilization_pct}%`}
          color={hours_this_month.utilization_pct >= 80 ? 'green' : hours_this_month.utilization_pct >= 50 ? 'gold' : 'red'}
        />
        <StatCard
          icon={ListChecks}
          label={isSpanish ? "Tareas Completadas" : "Tasks Completed"}
          value={tasks.completed}
          subValue={`${tasks.total} ${isSpanish ? 'total' : 'total'}`}
          color="green"
        />
        <StatCard
          icon={Warning}
          label={isSpanish ? "Tareas Atrasadas" : "Overdue Tasks"}
          value={tasks.overdue}
          color={tasks.overdue > 0 ? 'red' : 'cyan'}
        />
        <StatCard
          icon={CurrencyDollar}
          label={isSpanish ? "Rentabilidad" : "Profitability"}
          value={`$${billing.profitability?.toLocaleString()}`}
          color={billing.profitability >= 0 ? 'green' : 'red'}
        />
      </div>

      {/* Billing & Hours Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="valhalla-card p-6">
          <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#3AB8D6]" />
            {isSpanish ? 'Resumen de Horas (Este Mes)' : 'Hours Summary (This Month)'}
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Horas Totales' : 'Total Hours'}</span>
              <span className="text-[#F3F4F6] font-semibold">{hours_this_month.total?.toFixed(1)}h</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Facturables' : 'Billable'}</span>
              <span className="text-emerald-400 font-semibold">{hours_this_month.billable?.toFixed(1)}h</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'No Facturables' : 'Non-Billable'}</span>
              <span className="text-[#F3F4F6] font-semibold">{hours_this_month.non_billable?.toFixed(1)}h</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Aprobadas' : 'Approved'}</span>
              <span className="text-[#3AB8D6] font-semibold">{hours_this_month.approved?.toFixed(1)}h</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Pendientes' : 'Pending'}</span>
              <span className="text-amber-400 font-semibold">{hours_this_month.pending?.toFixed(1)}h</span>
            </div>
          </div>
          {/* Utilization Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-[#9CA3AF]">{t('utilization')}</span>
              <span className="text-[#F3F4F6]">{hours_this_month.utilization_pct}%</span>
            </div>
            <Progress value={hours_this_month.utilization_pct} className="h-2" />
            <p className="text-xs text-[#6B7280] mt-1">{isSpanish ? 'Capacidad:' : 'Capacity:'} {hours_this_month.capacity}h</p>
          </div>
        </div>

        <div className="valhalla-card p-6">
          <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <CurrencyDollar className="w-5 h-5 text-[#E2B05E]" />
            {isSpanish ? 'Resumen de Facturación' : 'Billing Summary'}
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Costo Interno/Hora' : 'Internal Cost/Hour'}</span>
              <span className="text-[#F3F4F6] font-semibold">${billing.internal_cost_rate}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Tarifa de Facturación' : 'Billing Rate'}</span>
              <span className="text-[#E2B05E] font-semibold">${billing.billing_rate}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Costo Laboral Total' : 'Total Labor Cost'}</span>
              <span className="text-[#F3F4F6] font-semibold">${billing.total_labor_cost?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Valor Facturado' : 'Billed Value'}</span>
              <span className="text-emerald-400 font-semibold">${billing.total_billed_value?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-[#9CA3AF]">{isSpanish ? 'Rentabilidad' : 'Profitability'}</span>
              <span className={`font-semibold ${billing.profitability >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                ${billing.profitability?.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Skills & Technologies */}
      <div className="valhalla-card p-6">
        <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4">{isSpanish ? 'Habilidades y Tecnologías' : 'Skills & Technologies'}</h3>
        <div className="flex flex-wrap gap-2">
          {employee.skills?.map((skill, idx) => (
            <Badge key={idx} className="bg-[#3AB8D6]/20 text-[#3AB8D6]">{skill}</Badge>
          ))}
          {employee.technologies?.map((tech, idx) => (
            <Badge key={idx} className="bg-[#E2B05E]/20 text-[#E2B05E]">{tech}</Badge>
          ))}
        </div>
      </div>

      {/* Recent Projects */}
      {projects.list && projects.list.length > 0 && (
        <div className="valhalla-card p-6">
          <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <Folder className="w-5 h-5 text-[#3AB8D6]" />
            {isSpanish ? 'Proyectos Asignados' : 'Assigned Projects'}
          </h3>
          <div className="space-y-3">
            {projects.list.map((proj) => (
              <div key={proj._id} className="flex items-center justify-between py-3 px-4 bg-[#0B0E14] rounded-lg">
                <div>
                  <p className="text-[#F3F4F6] font-medium">{proj.name}</p>
                  <p className="text-xs text-[#9CA3AF]">{proj.description?.slice(0, 50)}...</p>
                </div>
                <Badge className={proj.status === 'activo' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}>
                  {proj.status}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// User Account Tab Component
function UserAccountTab({ dashboard, employeeId, onRefresh }) {
  const { t, isSpanish } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('empleado');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  if (!dashboard) return null;
  
  const { employee, user_account } = dashboard;
  
  const handleCreateAccount = async () => {
    setLoading(true);
    try {
      const response = await createEmployeeAccount(employeeId, selectedRole);
      toast.success(`${isSpanish ? 'Cuenta creada' : 'Account created'}: ${response.data.email}`);
      toast.info(`${isSpanish ? 'Contraseña temporal' : 'Temporary password'}: ${response.data.default_password}`);
      setShowCreateDialog(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleRemoveAccount = async () => {
    if (!confirm(isSpanish ? '¿Eliminar cuenta de usuario?' : 'Remove user account?')) return;
    setLoading(true);
    try {
      await removeEmployeeAccount(employeeId);
      toast.success(isSpanish ? 'Cuenta eliminada' : 'Account removed');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetPassword = async () => {
    setLoading(true);
    try {
      const response = await resetEmployeePassword(employeeId);
      toast.success(`${isSpanish ? 'Nueva contraseña' : 'New password'}: ${response.data.new_password}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleAccount = async () => {
    setLoading(true);
    try {
      const newStatus = !user_account.is_active;
      await updateEmployeeAccountStatus(employeeId, newStatus);
      toast.success(isSpanish ? (newStatus ? 'Cuenta activada' : 'Cuenta desactivada') : (newStatus ? 'Account enabled' : 'Account disabled'));
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleRoleChange = async (newRole) => {
    setLoading(true);
    try {
      await updateEmployeeAccountRole(employeeId, newRole);
      toast.success(isSpanish ? `Rol actualizado a ${newRole}` : `Role updated to ${newRole}`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="valhalla-card p-6">
        <h3 className="text-lg font-semibold text-[#F3F4F6] mb-6 flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#3AB8D6]" />
          {isSpanish ? 'Cuenta de Usuario del Sistema' : 'System User Account'}
        </h3>
        
        {user_account ? (
          <div className="space-y-6">
            {/* Account Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
                  <span className="text-[#9CA3AF]">Email</span>
                  <span className="text-[#F3F4F6]">{user_account.email}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
                  <span className="text-[#9CA3AF]">{t('role')}</span>
                  <Select value={user_account.role} onValueChange={handleRoleChange} disabled={loading}>
                    <SelectTrigger className="w-32 valhalla-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#151B26] border-[#242E42]">
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="gerencia">Gerencia</SelectItem>
                      <SelectItem value="pm">PM</SelectItem>
                      <SelectItem value="rrhh">RRHH</SelectItem>
                      <SelectItem value="empleado">Empleado</SelectItem>
                      <SelectItem value="cliente">Cliente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
                  <span className="text-[#9CA3AF]">{t('status')}</span>
                  <Badge className={user_account.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                    {user_account.is_active ? (isSpanish ? 'Activa' : 'Active') : (isSpanish ? 'Desactivada' : 'Disabled')}
                  </Badge>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#242E42]">
                  <span className="text-[#9CA3AF]">{isSpanish ? 'Último Login' : 'Last Login'}</span>
                  <span className="text-[#F3F4F6]">{user_account.last_login?.split('T')[0] || 'Never'}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-[#9CA3AF]">{isSpanish ? 'Creada' : 'Created'}</span>
                  <span className="text-[#F3F4F6]">{user_account.created_at?.split('T')[0]}</span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="space-y-3">
                <h4 className="text-[#9CA3AF] text-sm mb-3">{isSpanish ? 'Acciones de Cuenta' : 'Account Actions'}</h4>
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-[#242E42]" 
                  onClick={handleToggleAccount}
                  disabled={loading}
                >
                  <Power className="w-4 h-4 mr-2" />
                  {user_account.is_active ? (isSpanish ? 'Desactivar Cuenta' : 'Disable Account') : (isSpanish ? 'Activar Cuenta' : 'Enable Account')}
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-[#242E42]" 
                  onClick={handleResetPassword}
                  disabled={loading}
                >
                  <LockKey className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Resetear Contraseña' : 'Reset Password'}
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-red-500/30 text-red-400 hover:bg-red-500/10" 
                  onClick={handleRemoveAccount}
                  disabled={loading}
                >
                  <UserCircleMinus className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Eliminar Cuenta' : 'Remove Account'}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <User className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#9CA3AF] mb-4">
              {isSpanish ? 'Este empleado no tiene cuenta de usuario en el sistema.' : 'This employee does not have a system user account.'}
            </p>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]">
                  <UserCirclePlus className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Crear Cuenta de Usuario' : 'Create User Account'}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#151B26] border-[#242E42]">
                <DialogHeader>
                  <DialogTitle className="text-[#F3F4F6]">
                    {isSpanish ? 'Crear Cuenta de Usuario' : 'Create User Account'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <p className="text-[#9CA3AF]">
                    {isSpanish ? 'Se creará una cuenta para:' : 'Creating account for:'} <span className="text-[#F3F4F6]">{employee.email}</span>
                  </p>
                  <div className="space-y-2">
                    <label className="text-[#9CA3AF] text-sm">{t('role')}</label>
                    <Select value={selectedRole} onValueChange={setSelectedRole}>
                      <SelectTrigger className="valhalla-input">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#151B26] border-[#242E42]">
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="gerencia">Gerencia</SelectItem>
                        <SelectItem value="pm">PM</SelectItem>
                        <SelectItem value="rrhh">RRHH</SelectItem>
                        <SelectItem value="empleado">Empleado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-[#6B7280]">
                    {isSpanish ? 'Se generará una contraseña temporal que el usuario deberá cambiar.' : 'A temporary password will be generated that the user must change.'}
                  </p>
                  <Button 
                    className="w-full bg-[#E2B05E] text-[#0B0E14] hover:bg-[#F0C47A]"
                    onClick={handleCreateAccount}
                    disabled={loading}
                  >
                    {loading ? t('loading') : (isSpanish ? 'Crear Cuenta' : 'Create Account')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
      
      {/* Permissions Matrix */}
      <div className="valhalla-card p-6">
        <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4">{isSpanish ? 'Matriz de Permisos por Rol' : 'Role Permissions Matrix'}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#242E42]">
                <th className="text-left py-3 px-4 text-[#9CA3AF]">{isSpanish ? 'Funcionalidad' : 'Feature'}</th>
                <th className="text-center py-3 px-4 text-[#9CA3AF]">Admin</th>
                <th className="text-center py-3 px-4 text-[#9CA3AF]">Gerencia</th>
                <th className="text-center py-3 px-4 text-[#9CA3AF]">PM</th>
                <th className="text-center py-3 px-4 text-[#9CA3AF]">RRHH</th>
                <th className="text-center py-3 px-4 text-[#9CA3AF]">Empleado</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: isSpanish ? 'Dashboard Financiero' : 'Financial Dashboard', admin: true, gerencia: true, pm: false, rrhh: false, empleado: false },
                { feature: isSpanish ? 'Gestión de Clientes' : 'Client Management', admin: true, gerencia: true, pm: true, rrhh: false, empleado: false },
                { feature: isSpanish ? 'Gestión de Proyectos' : 'Project Management', admin: true, gerencia: true, pm: true, rrhh: false, empleado: false },
                { feature: isSpanish ? 'Gestión de Empleados' : 'Employee Management', admin: true, gerencia: false, pm: false, rrhh: true, empleado: false },
                { feature: isSpanish ? 'Usuarios del Sistema' : 'System Users', admin: true, gerencia: false, pm: false, rrhh: false, empleado: false },
                { feature: isSpanish ? 'Aprobar Horas' : 'Approve Hours', admin: true, gerencia: true, pm: true, rrhh: false, empleado: false },
                { feature: isSpanish ? 'Facturación' : 'Invoicing', admin: true, gerencia: true, pm: false, rrhh: false, empleado: false },
                { feature: isSpanish ? 'Nómina' : 'Payroll', admin: true, gerencia: true, pm: false, rrhh: true, empleado: false },
                { feature: isSpanish ? 'Tareas Propias' : 'Own Tasks', admin: true, gerencia: true, pm: true, rrhh: true, empleado: true },
                { feature: isSpanish ? 'Registrar Horas' : 'Log Hours', admin: true, gerencia: true, pm: true, rrhh: true, empleado: true },
              ].map((row, idx) => (
                <tr key={idx} className="border-b border-[#242E42]/50">
                  <td className="py-3 px-4 text-[#F3F4F6]">{row.feature}</td>
                  <td className="text-center py-3 px-4">{row.admin ? <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" /> : '-'}</td>
                  <td className="text-center py-3 px-4">{row.gerencia ? <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" /> : '-'}</td>
                  <td className="text-center py-3 px-4">{row.pm ? <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" /> : '-'}</td>
                  <td className="text-center py-3 px-4">{row.rrhh ? <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" /> : '-'}</td>
                  <td className="text-center py-3 px-4">{row.empleado ? <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" /> : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Timesheets Tab Component
function TimesheetsTab({ employeeId }) {
  const { t, isSpanish } = useLanguage();
  const [data, setData] = useState({ entries: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('all');

  const fetchTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (status !== 'all') params.status = status;
      const response = await getEmployeeTimesheets(employeeId, params);
      setData(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, employeeId, status, t]);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="space-y-2">
          <label className="text-[#9CA3AF] text-sm">{isSpanish ? 'Desde' : 'From'}</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="valhalla-input w-40" />
        </div>
        <div className="space-y-2">
          <label className="text-[#9CA3AF] text-sm">{isSpanish ? 'Hasta' : 'To'}</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="valhalla-input w-40" />
        </div>
        <div className="space-y-2">
          <label className="text-[#9CA3AF] text-sm">{t('status')}</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-36 valhalla-input">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#151B26] border-[#242E42]">
              <SelectItem value="all">{t('all')}</SelectItem>
              <SelectItem value="pendiente">{isSpanish ? 'Pendiente' : 'Pending'}</SelectItem>
              <SelectItem value="aprobada">{isSpanish ? 'Aprobada' : 'Approved'}</SelectItem>
              <SelectItem value="rechazada">{isSpanish ? 'Rechazada' : 'Rejected'}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard icon={Clock} label={isSpanish ? 'Total' : 'Total'} value={`${data.summary?.total_hours?.toFixed(1) || 0}h`} color="cyan" />
        <StatCard icon={CurrencyDollar} label={isSpanish ? 'Facturables' : 'Billable'} value={`${data.summary?.billable_hours?.toFixed(1) || 0}h`} color="green" />
        <StatCard icon={CheckCircle} label={isSpanish ? 'Aprobadas' : 'Approved'} value={`${data.summary?.approved_hours?.toFixed(1) || 0}h`} color="gold" />
        <StatCard icon={Clock} label={isSpanish ? 'Pendientes' : 'Pending'} value={`${data.summary?.pending_hours?.toFixed(1) || 0}h`} color="purple" />
        <StatCard icon={Warning} label={isSpanish ? 'Rechazadas' : 'Rejected'} value={`${data.summary?.rejected_hours?.toFixed(1) || 0}h`} color="red" />
      </div>

      {/* Entries Table */}
      {loading ? (
        <div className="text-center py-8 text-[#9CA3AF]">{t('loading')}</div>
      ) : data.entries.length === 0 ? (
        <div className="valhalla-card p-12 text-center">
          <Clock className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
          <p className="text-[#9CA3AF]">{t('noData')}</p>
        </div>
      ) : (
        <div className="valhalla-card overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#0B0E14]">
              <tr>
                <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{t('date')}</th>
                <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Proyecto' : 'Project'}</th>
                <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{isSpanish ? 'Cliente' : 'Client'}</th>
                <th className="text-left py-4 px-6 text-[#9CA3AF] font-medium">{t('description')}</th>
                <th className="text-right py-4 px-6 text-[#9CA3AF] font-medium">{t('hours')}</th>
                <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{t('billable')}</th>
                <th className="text-center py-4 px-6 text-[#9CA3AF] font-medium">{t('status')}</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((entry) => (
                <tr key={entry._id} className="border-t border-[#242E42] hover:bg-[#0B0E14]/50">
                  <td className="py-4 px-6 text-[#F3F4F6]">{entry.date}</td>
                  <td className="py-4 px-6 text-[#9CA3AF]">{entry.project_name || '-'}</td>
                  <td className="py-4 px-6 text-[#9CA3AF]">{entry.client_name || '-'}</td>
                  <td className="py-4 px-6 text-[#9CA3AF] max-w-xs truncate">{entry.description}</td>
                  <td className="py-4 px-6 text-right text-[#F3F4F6] font-medium">{entry.hours}h</td>
                  <td className="py-4 px-6 text-center">
                    <Badge className={entry.is_billable ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}>
                      {entry.is_billable ? t('billable') : t('nonBillable')}
                    </Badge>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <Badge className={entry.status === 'aprobada' ? 'bg-emerald-500/20 text-emerald-400' : entry.status === 'rechazada' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}>
                      {entry.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Status Management Tab
function StatusTab({ dashboard, employeeId, onRefresh }) {
  const { t, isSpanish } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  
  if (!dashboard) return null;
  
  const { employee } = dashboard;
  
  const handleStatusChange = async (newStatus) => {
    if (!confirm(isSpanish ? `¿Cambiar estado a ${newStatus}?` : `Change status to ${newStatus}?`)) return;
    setLoading(true);
    try {
      await updateEmployeeStatus(employeeId, newStatus, reason);
      toast.success(isSpanish ? 'Estado actualizado' : 'Status updated');
      setReason('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleRestore = async () => {
    setLoading(true);
    try {
      await restoreEmployee(employeeId);
      toast.success(isSpanish ? 'Empleado restaurado' : 'Employee restored');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };
  
  const statuses = [
    { value: 'activo', label: isSpanish ? 'Activo' : 'Active', color: 'emerald', description: isSpanish ? 'Empleado trabajando normalmente' : 'Employee working normally' },
    { value: 'inactivo', label: isSpanish ? 'Inactivo' : 'Inactive', color: 'gray', description: isSpanish ? 'Sin actividad temporal' : 'Temporary inactivity' },
    { value: 'suspendido', label: isSpanish ? 'Suspendido' : 'Suspended', color: 'amber', description: isSpanish ? 'Suspensión disciplinaria' : 'Disciplinary suspension' },
    { value: 'en_licencia', label: isSpanish ? 'En Licencia' : 'On Leave', color: 'blue', description: isSpanish ? 'Licencia por maternidad, enfermedad, etc.' : 'Maternity, sick leave, etc.' },
    { value: 'terminado', label: isSpanish ? 'Terminado' : 'Terminated', color: 'red', description: isSpanish ? 'Contrato terminado' : 'Contract terminated' },
    { value: 'archivado', label: isSpanish ? 'Archivado' : 'Archived', color: 'gray', description: isSpanish ? 'Registro archivado (soft delete)' : 'Record archived (soft delete)' },
  ];
  
  return (
    <div className="space-y-6">
      <div className="valhalla-card p-6">
        <h3 className="text-lg font-semibold text-[#F3F4F6] mb-6 flex items-center gap-2">
          <Gear className="w-5 h-5 text-[#3AB8D6]" />
          {isSpanish ? 'Gestión de Estado del Empleado' : 'Employee Status Management'}
        </h3>
        
        <div className="mb-6">
          <p className="text-[#9CA3AF] mb-2">{isSpanish ? 'Estado actual:' : 'Current status:'}</p>
          <Badge className={`text-lg px-4 py-2 ${
            employee.status === 'activo' ? 'bg-emerald-500/20 text-emerald-400' :
            employee.status === 'archivado' ? 'bg-gray-500/20 text-gray-400' :
            employee.status === 'terminado' ? 'bg-red-500/20 text-red-400' :
            'bg-amber-500/20 text-amber-400'
          }`}>
            {employee.status}
          </Badge>
        </div>
        
        {employee.status === 'archivado' && (
          <div className="mb-6 p-4 bg-[#0B0E14] rounded-lg border border-[#242E42]">
            <p className="text-[#9CA3AF] mb-3">{isSpanish ? 'Este empleado está archivado.' : 'This employee is archived.'}</p>
            <Button onClick={handleRestore} disabled={loading} className="bg-[#3AB8D6] text-white hover:bg-[#2A8FA8]">
              <ArrowCounterClockwise className="w-4 h-4 mr-2" />
              {isSpanish ? 'Restaurar Empleado' : 'Restore Employee'}
            </Button>
          </div>
        )}
        
        <div className="space-y-4 mb-6">
          <label className="text-[#9CA3AF] text-sm">{isSpanish ? 'Razón del cambio (opcional)' : 'Reason for change (optional)'}</label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={isSpanish ? 'Ingrese razón...' : 'Enter reason...'}
            className="valhalla-input"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {statuses.filter(s => s.value !== employee.status).map((status) => (
            <Button
              key={status.value}
              variant="outline"
              className={`justify-start h-auto py-4 border-[#242E42] hover:border-${status.color}-500/50`}
              onClick={() => handleStatusChange(status.value)}
              disabled={loading}
            >
              <div className="text-left">
                <div className={`text-${status.color}-400 font-medium`}>{status.label}</div>
                <div className="text-xs text-[#6B7280] mt-1">{status.description}</div>
              </div>
            </Button>
          ))}
        </div>
      </div>
      
      {/* Status History */}
      {employee.status_history && employee.status_history.length > 0 && (
        <div className="valhalla-card p-6">
          <h3 className="text-lg font-semibold text-[#F3F4F6] mb-4">{isSpanish ? 'Historial de Estados' : 'Status History'}</h3>
          <div className="space-y-3">
            {employee.status_history.slice().reverse().map((history, idx) => (
              <div key={idx} className="flex items-start gap-4 py-3 border-b border-[#242E42] last:border-0">
                <div className={`w-3 h-3 rounded-full mt-1 ${
                  history.status === 'activo' ? 'bg-emerald-400' :
                  history.status === 'archivado' ? 'bg-gray-400' :
                  'bg-amber-400'
                }`}></div>
                <div className="flex-1">
                  <p className="text-[#F3F4F6] font-medium">{history.status}</p>
                  <p className="text-sm text-[#9CA3AF]">{history.reason}</p>
                  <p className="text-xs text-[#6B7280] mt-1">{history.changed_at?.split('T')[0]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Main Page Component
export function EmployeeDetailPage() {
  const { t, isSpanish } = useLanguage();
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getEmployeeDashboard(employeeId);
      setDashboard(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  }, [employeeId, navigate, t]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const employeeName = dashboard?.employee ? `${dashboard.employee.name} ${dashboard.employee.last_name}` : t('employeeDetails');

  return (
    <Layout title={employeeName}>
      <div className="space-y-6" data-testid="employee-detail-page">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/employees')} className="text-[#9CA3AF] hover:text-[#F3F4F6]">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {isSpanish ? 'Volver a Empleados' : 'Back to Employees'}
        </Button>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#0B0E14] border border-[#242E42] flex-wrap h-auto p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <User className="w-4 h-4 mr-2" />
              {isSpanish ? 'Resumen' : 'Overview'}
            </TabsTrigger>
            <TabsTrigger value="account" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <Shield className="w-4 h-4 mr-2" />
              {isSpanish ? 'Cuenta de Usuario' : 'User Account'}
            </TabsTrigger>
            <TabsTrigger value="timesheets" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <Clock className="w-4 h-4 mr-2" />
              {isSpanish ? 'Timesheets' : 'Timesheets'}
            </TabsTrigger>
            <TabsTrigger value="status" className="data-[state=active]:bg-[#E2B05E] data-[state=active]:text-[#0B0E14]">
              <Gear className="w-4 h-4 mr-2" />
              {isSpanish ? 'Estado' : 'Status'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <OverviewTab dashboard={dashboard} loading={loading} onRefresh={fetchDashboard} />
          </TabsContent>
          <TabsContent value="account" className="mt-6">
            <UserAccountTab dashboard={dashboard} employeeId={employeeId} onRefresh={fetchDashboard} />
          </TabsContent>
          <TabsContent value="timesheets" className="mt-6">
            <TimesheetsTab employeeId={employeeId} />
          </TabsContent>
          <TabsContent value="status" className="mt-6">
            <StatusTab dashboard={dashboard} employeeId={employeeId} onRefresh={fetchDashboard} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export default EmployeeDetailPage;
